import { useState } from 'react';
import { Page } from './types';
import { SEASON_STATS } from './data/mockData';
import { useGames } from './hooks/useGames';
import NavBar from './components/NavBar';
import BottomNav from './components/BottomNav';
import Today from './pages/Today';
import History from './pages/History';
import About from './pages/About';

const NAV_ITEMS: { id: Page; label: string; icon: string }[] = [
  { id: 'today',   label: 'Today',   icon: '⚾' },
  { id: 'history', label: 'History', icon: '📋' },
  { id: 'about',   label: 'Model',   icon: '📊' },
];

export default function App() {
  const [page, setPage] = useState<Page>('today');
  const { games } = useGames();

  const avoidGames = [...games]
    .sort((a, b) => a.prob - b.prob)
    .slice(0, 3);

  const topGame = [...games]
    .sort((a, b) => b.prob - a.prob)
    .find(g => g.strong);

  return (
    <>
      {/* ── MOBILE ── */}
      <div className="mobile-only" style={{ display: 'block' }}>
        <style>{`
          @media (min-width: 769px) { .mobile-only { display: none !important; } }
        `}</style>
        <NavBar />
        <main style={{ paddingBottom: 72 }}>
          {page === 'today'   && <Today />}
          {page === 'history' && <History />}
          {page === 'about'   && <About />}
        </main>
        <BottomNav page={page} setPage={setPage} />
      </div>

      {/* ── DESKTOP ── */}
      <div className="desktop-only" style={{ display: 'none' }}>
        <style>{`
          @media (min-width: 769px) { .desktop-only { display: flex !important; min-height: 100vh; } }
        `}</style>

        {/* LEFT SIDEBAR */}
        <div className="desktop-sidebar">
          <div className="desktop-sidebar-logo">
            No
            <span style={{ fontWeight: 300, color: '#D42B2B', margin: '0 3px' }}>
              /
            </span>
            Dingers
          </div>

          <nav className="desktop-sidebar-nav">
            {NAV_ITEMS.map(item => (
              <button
                key={item.id}
                onClick={() => setPage(item.id)}
                className={`desktop-nav-item ${page === item.id ? 'active' : ''}`}
              >
                <span className="desktop-nav-icon">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </nav>

          <div className="desktop-sidebar-footer">
            MLB Stats API · Odds API<br />
            Open-Meteo Weather<br />
            Updated daily · 2026 Season<br /><br />
            <span>Free season · Not financial advice.</span>
          </div>
        </div>

        {/* CENTER — phone frame */}
        <div className="desktop-phone-frame">
          <div className="desktop-phone-inner">
            <NavBar />
            <main style={{ paddingBottom: 72 }}>
              {page === 'today'   && <Today />}
              {page === 'history' && <History />}
              {page === 'about'   && <About />}
            </main>
            <BottomNav page={page} setPage={setPage} />
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="desktop-right-panel">

          {/* season stats */}
          <div>
            <div className="desktop-panel-title">Season 2026</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="desktop-stat-card">
                <div className="desktop-stat-val green">
                  {SEASON_STATS.accuracy > 0 ? `${SEASON_STATS.accuracy}%` : '—'}
                </div>
                <div className="desktop-stat-lbl">Model Accuracy</div>
              </div>
              <div className="desktop-stat-card">
                <div className="desktop-stat-val red">
                  {SEASON_STATS.rate > 0 ? `${SEASON_STATS.rate}%` : '—'}
                </div>
                <div className="desktop-stat-lbl">No-HR Rate</div>
              </div>
              <div className="desktop-stat-card">
                <div className="desktop-stat-val blue">
                  {SEASON_STATS.played > 0 ? SEASON_STATS.played : '—'}
                </div>
                <div className="desktop-stat-lbl">Games Tracked</div>
              </div>
              <div className="desktop-stat-card">
                <div className="desktop-stat-val green">
                  {SEASON_STATS.played > 0 ? SEASON_STATS.pl : '—'}
                </div>
                <div className="desktop-stat-lbl">Season P/L</div>
              </div>
            </div>
          </div>

          {/* best bet */}
          {topGame && (
            <div>
              <div className="desktop-panel-title">Best Bet Today</div>
              <div className="desktop-stat-card" style={{ borderColor: '#15803D' }}>
                <div style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: '2.5rem',
                  color: '#15803D',
                  lineHeight: 1,
                }}>
                  {topGame.prob}%
                </div>
                <div style={{
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  color: '#0A1740',
                  marginTop: 4,
                }}>
                  {topGame.awayAbbr} @ {topGame.homeAbbr}
                </div>
                <div style={{
                  fontSize: '0.68rem',
                  color: '#8090B0',
                  marginTop: 2,
                }}>
                  {topGame.venue} · {topGame.time}
                </div>
                <div style={{
                  fontSize: '0.68rem',
                  color: '#8090B0',
                  marginTop: 2,
                }}>
                  {topGame.awayPitcher.split(' ').pop()} vs {topGame.homePitcher.split(' ').pop()}
                </div>
              </div>
            </div>
          )}

          {/* model note */}
          <div className="desktop-model-note">
            5-factor model: park factor, SP HR/9, team SLG, game total, and weather — updated daily before first pitch.
          </div>

          {/* avoid today */}
          {avoidGames.length > 0 && (
            <div>
              <div className="desktop-panel-title">Avoid Today</div>
              <div className="desktop-avoid-card">
                {avoidGames.map(g => (
                  <div key={g.id} className="desktop-avoid-item">
                    <span>{g.awayAbbr} @ {g.homeAbbr}</span>
                    <span style={{ color: '#D42B2B', fontWeight: 700 }}>
                      {g.prob}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
}
